/**
 * Influencers.jsx — Directory (CRM) for creators.
 *
 * Views:
 *  - KPI strip (totals, tier distribution)
 *  - Filters (search, platform, niche, tier, follower range, ER%, status, tag)
 *  - Sortable table with quick edit + inspect drawer
 *  - CSV bulk import
 *
 * Tabs at page-level: Directory · Campaigns · Payments · Dashboard
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import {
  Users2, Search, Filter, Plus, Upload, X, Instagram, Youtube,
  Twitter, Linkedin, ArrowUpRight, TrendingUp, IndianRupee, Loader2,
  Wand2, BarChart3,
} from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

const TIERS = [
  { v: "", label: "Any tier" },
  { v: "nano", label: "Nano (<10K)" },
  { v: "micro", label: "Micro (10K–100K)" },
  { v: "mid", label: "Mid (100K–500K)" },
  { v: "macro", label: "Macro (500K–1M)" },
  { v: "mega", label: "Mega (1M+)" },
];
const PLATFORM_ICONS = {
  instagram: Instagram, youtube: Youtube, twitter: Twitter, linkedin: Linkedin,
};
const TIER_COLORS = {
  nano:  "border-neutral-400 text-neutral-700 bg-neutral-100/50",
  micro: "border-emerald-400 text-emerald-700 bg-emerald-50",
  mid:   "border-sky-500 text-sky-700 bg-sky-50",
  macro: "border-violet-500 text-violet-700 bg-violet-50",
  mega:  "border-amber-500 text-amber-700 bg-amber-50",
};

function fmtFollowers(n) {
  if (!n) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export default function Influencers() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [dash, setDash] = useState(null);
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [platform, setPlatform] = useState("");
  const [niche, setNiche] = useState("");
  const [tier, setTier] = useState("");
  const [status, setStatus] = useState("");
  const [minF, setMinF] = useState("");
  const [maxF, setMaxF] = useState("");
  const [sort, setSort] = useState("newest");
  const [offset, setOffset] = useState(0);
  const PAGE = 50;

  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showAi, setShowAi] = useState(false);
  const [showBench, setShowBench] = useState(false);

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (platform) params.set("platform", platform);
      if (niche) params.set("niche", niche);
      if (tier) params.set("tier", tier);
      if (status) params.set("status", status);
      if (minF) params.set("followers_min", minF);
      if (maxF) params.set("followers_max", maxF);
      params.set("sort", sort);
      params.set("limit", String(PAGE));
      params.set("offset", String(offset));
      const { data } = await api.get(`/workspaces/${wsId}/influencers?${params}`);
      setRows(data?.rows || []);
      setTotal(data?.total || 0);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
    finally { setLoading(false); }
  }, [wsId, q, platform, niche, tier, status, minF, maxF, sort, offset]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOffset(0); }, [q, platform, niche, tier, status, minF, maxF, sort]);

  useEffect(() => {
    if (!wsId) return;
    Promise.all([
      api.get(`/workspaces/${wsId}/influencers/facets`).then((r) => setFacets(r.data || {})).catch(() => {}),
      api.get(`/workspaces/${wsId}/influencer-dashboard`).then((r) => setDash(r.data || null)).catch(() => {}),
    ]);
  }, [wsId]);

  const clearFilters = () => {
    setQ(""); setPlatform(""); setNiche(""); setTier(""); setStatus("");
    setMinF(""); setMaxF(""); setSort("newest"); setOffset(0);
  };

  const t = dash?.totals || {};
  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="influencers-page">
      <PageHeader
        title="Influencer marketing"
        icon={Users2}
        description="Manage creators, campaigns, content approvals and payments."
        actions={(
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm" data-testid="inf-nav-campaigns">
              <Link to="/app/influencers/campaigns"><TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Campaigns</Link>
            </Button>
            <Button asChild variant="outline" size="sm" data-testid="inf-nav-payments">
              <Link to="/app/influencers/payments"><IndianRupee className="w-3.5 h-3.5 mr-1.5" /> Payments</Link>
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowAi(true)} data-testid="inf-ai-discover-open">
              <Wand2 className="w-3.5 h-3.5 mr-1.5" /> AI find
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowBench(true)} data-testid="inf-bench-open">
              <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Rate benchmark
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="inf-import-open">
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Import CSV
            </Button>
            <Button size="sm" onClick={() => setShowAdd(true)} data-testid="inf-add-open">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add creator
            </Button>
          </div>
        )}
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Creators" value={t.influencers ?? "—"} testid="inf-kpi-total" />
        <Kpi label="Active campaigns" value={t.active_campaigns ?? "—"} testid="inf-kpi-active" />
        <Kpi label="Completed" value={t.completed_campaigns ?? "—"} testid="inf-kpi-done" />
        <Kpi label="Outstanding" value={`₹${(t.outstanding_inr ?? 0).toLocaleString("en-IN")}`}
             tone={t.outstanding_inr > 0 ? "warn" : "neutral"} testid="inf-kpi-outstanding" />
        <Kpi label="Unpaid participants" value={t.unpaid_participant_count ?? "—"} testid="inf-kpi-unpaid" />
      </div>

      {/* Filters */}
      <div className="border border-border p-3 space-y-3" data-testid="inf-filters">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
          <Filter className="w-3.5 h-3.5" /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <div className="md:col-span-2 lg:col-span-2">
            <Label className="text-[10px] uppercase text-muted-foreground">Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-9 text-xs pl-8"
                     placeholder="Name or @handle…" data-testid="inf-search" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Platform</Label>
            <Select value={platform || "__all__"} onValueChange={(v) => setPlatform(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9 text-xs" data-testid="inf-filter-platform"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Any platform</SelectItem>
                {(facets.platforms || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Tier</Label>
            <Select value={tier || "__all__"} onValueChange={(v) => setTier(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9 text-xs" data-testid="inf-filter-tier"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIERS.map((t) => <SelectItem key={t.v || "__all__"} value={t.v || "__all__"}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Niche</Label>
            <Select value={niche || "__all__"} onValueChange={(v) => setNiche(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9 text-xs" data-testid="inf-filter-niche"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Any niche</SelectItem>
                {(facets.niches || []).map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Status</Label>
            <Select value={status || "__all__"} onValueChange={(v) => setStatus(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9 text-xs" data-testid="inf-filter-status"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Any status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="prospect">Prospect</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="blacklisted">Blacklisted</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Followers min</Label>
            <Input type="number" value={minF} onChange={(e) => setMinF(e.target.value)}
                   placeholder="10000" className="h-9 text-xs" data-testid="inf-filter-min" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Followers max</Label>
            <Input type="number" value={maxF} onChange={(e) => setMaxF(e.target.value)}
                   placeholder="500000" className="h-9 text-xs" data-testid="inf-filter-max" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Sort</Label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-9 text-xs" data-testid="inf-filter-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest first</SelectItem>
                <SelectItem value="oldest">Oldest first</SelectItem>
                <SelectItem value="name">Name A→Z</SelectItem>
                <SelectItem value="followers_desc">Followers ↓</SelectItem>
                <SelectItem value="followers_asc">Followers ↑</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button size="sm" variant="outline" onClick={clearFilters} className="w-full h-9" data-testid="inf-clear">
              <X className="w-3.5 h-3.5 mr-1.5" /> Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="p-6 text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Users2} title="No creators match"
                    description="Try widening the filters, or add your first creator to start building the directory."
                    testId="inf-empty" />
      ) : (
        <div className="border border-border overflow-x-auto" data-testid="inf-table">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 border-b border-border">
              <tr className="text-left text-[10px] uppercase text-muted-foreground">
                <th className="px-3 py-2">Creator</th>
                <th className="px-3 py-2">Platforms</th>
                <th className="px-3 py-2 text-right">Peak followers</th>
                <th className="px-3 py-2">Tier</th>
                <th className="px-3 py-2">Niches</th>
                <th className="px-3 py-2">Rate card (INR)</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-muted/30" data-testid={`inf-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <Link to={`/app/influencers/${r.id}`} className="flex items-center gap-2 hover:text-primary">
                      {r.avatar_url ? (
                        <img src={r.avatar_url} alt="" className="w-8 h-8 border border-border object-cover" />
                      ) : (
                        <div className="w-8 h-8 bg-secondary flex items-center justify-center text-xs font-bold">
                          {r.name?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-medium truncate">{r.name}</div>
                        <div className="text-[10px] text-muted-foreground truncate">@{r.primary_handle}</div>
                      </div>
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1 flex-wrap">
                      {(r.platforms || []).slice(0, 4).map((p, i) => {
                        const Icon = PLATFORM_ICONS[p.platform];
                        return (
                          <span key={i} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border border-border"
                                title={`${p.platform}: ${fmtFollowers(p.followers)}`}>
                            {Icon ? <Icon className="w-3 h-3" /> : <span className="uppercase font-mono">{p.platform.slice(0, 3)}</span>}
                            {fmtFollowers(p.followers)}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {fmtFollowers(r.peak_followers)}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-1.5 py-0.5 border text-[10px] uppercase tracking-wide ${TIER_COLORS[r.tier] || TIER_COLORS.nano}`}>
                      {r.tier}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1 max-w-[200px]">
                      {(r.niches || []).slice(0, 3).map((n) => (
                        <span key={n} className="text-[10px] px-1 border border-border">{n}</span>
                      ))}
                      {r.niches?.length > 3 && (
                        <span className="text-[10px] text-muted-foreground">+{r.niches.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-[11px] tabular-nums text-muted-foreground">
                    {r.rate_card?.per_reel_inr ? `R:₹${(r.rate_card.per_reel_inr / 1000).toFixed(0)}K ` : ""}
                    {r.rate_card?.per_post_inr ? `P:₹${(r.rate_card.per_post_inr / 1000).toFixed(0)}K` : ""}
                    {!r.rate_card?.per_reel_inr && !r.rate_card?.per_post_inr && "—"}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase tracking-wide ${
                      r.status === "active" ? "text-emerald-600"
                      : r.status === "blacklisted" ? "text-rose-600"
                      : "text-muted-foreground"
                    }`}>{r.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/app/influencers/${r.id}`} className="text-muted-foreground hover:text-primary" data-testid={`inf-open-${r.id}`}>
                      <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div>Showing {offset + 1}–{offset + rows.length} of {total}</div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={offset <= 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE))} data-testid="inf-prev">Previous</Button>
            <Button size="sm" variant="outline" disabled={offset + rows.length >= total}
                    onClick={() => setOffset(offset + PAGE)} data-testid="inf-next">Next</Button>
          </div>
        </div>
      )}

      {showAdd && <AddInfluencerDialog wsId={wsId} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {showImport && <ImportDialog wsId={wsId} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />}
      {showAi && <AiDiscoveryDialog wsId={wsId} onClose={() => setShowAi(false)} />}
      {showBench && <RateBenchmarkDialog wsId={wsId} onClose={() => setShowBench(false)} />}
    </div>
  );
}

function Kpi({ label, value, tone, testid }) {
  const cls = tone === "warn" ? "border-amber-400 bg-amber-50/50 text-amber-900" : "border-border";
  return (
    <div className={`border ${cls} p-3`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}

function AddInfluencerDialog({ wsId, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [followers, setFollowers] = useState("");
  const [er, setEr] = useState("");
  const [niches, setNiches] = useState("");
  const [emails, setEmails] = useState("");
  const [ratePost, setRatePost] = useState("");
  const [rateReel, setRateReel] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name || !handle) { toast.error("Name and handle required"); return; }
    setSaving(true);
    try {
      await api.post(`/workspaces/${wsId}/influencers`, {
        name,
        primary_handle: handle,
        platforms: [{
          platform, handle,
          followers: Number(followers) || 0,
          engagement_pct: Number(er) || undefined,
        }],
        niches: niches.split(",").map((s) => s.trim()).filter(Boolean),
        emails: emails.split(",").map((s) => s.trim()).filter(Boolean),
        rate_card: {
          per_post_inr: Number(ratePost) || undefined,
          per_reel_inr: Number(rateReel) || undefined,
        },
      });
      toast.success("Creator added");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="inf-add-dialog">
        <DialogHeader><DialogTitle>Add creator</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-[10px] uppercase">Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 text-xs" data-testid="inf-add-name" /></div>
            <div><Label className="text-[10px] uppercase">Handle *</Label><Input value={handle} onChange={(e) => setHandle(e.target.value)} className="h-9 text-xs" placeholder="@handle" data-testid="inf-add-handle" /></div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] uppercase">Platform</Label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger className="h-9 text-xs" data-testid="inf-add-platform"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["instagram", "youtube", "tiktok", "linkedin", "twitter", "facebook", "snapchat", "pinterest", "twitch", "threads", "custom"].map((p) =>
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-[10px] uppercase">Followers</Label><Input type="number" value={followers} onChange={(e) => setFollowers(e.target.value)} className="h-9 text-xs" data-testid="inf-add-followers" /></div>
            <div><Label className="text-[10px] uppercase">ER %</Label><Input type="number" step="0.1" value={er} onChange={(e) => setEr(e.target.value)} className="h-9 text-xs" /></div>
          </div>
          <div><Label className="text-[10px] uppercase">Niches (comma-sep)</Label><Input value={niches} onChange={(e) => setNiches(e.target.value)} className="h-9 text-xs" placeholder="lifestyle, food" /></div>
          <div><Label className="text-[10px] uppercase">Emails (comma-sep)</Label><Input value={emails} onChange={(e) => setEmails(e.target.value)} className="h-9 text-xs" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-[10px] uppercase">Rate per post (₹)</Label><Input type="number" value={ratePost} onChange={(e) => setRatePost(e.target.value)} className="h-9 text-xs" /></div>
            <div><Label className="text-[10px] uppercase">Rate per reel (₹)</Label><Input type="number" value={rateReel} onChange={(e) => setRateReel(e.target.value)} className="h-9 text-xs" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="inf-add-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ wsId, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!file) { toast.error("Choose a CSV file"); return; }
    setImporting(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/workspaces/${wsId}/influencers/import-csv`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported: ${data.created} new · ${data.updated} updated · ${data.skipped} skipped`);
    } catch (e) { toast.error(apiErrorMessage(e, "Import failed")); }
    finally { setImporting(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="inf-import-dialog">
        <DialogHeader><DialogTitle>Bulk import creators (CSV)</DialogTitle></DialogHeader>
        <div className="space-y-3 text-xs">
          <div className="text-muted-foreground">
            Expected columns:
            <code className="block mt-1 text-[10px] bg-muted p-2 whitespace-pre-wrap">
              name, primary_handle, platform, followers, engagement_pct, niches (;-sep),
              tier, country, city, per_post_inr, per_reel_inr, per_story_inr,
              per_video_inr, emails (;-sep), phones (;-sep), tags (;-sep), status, notes
            </code>
            <div className="mt-1">Existing handles are updated in place.</div>
          </div>
          <Input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0])} data-testid="inf-import-file" />
          {result && (
            <div className="border border-border p-3 space-y-1 text-[11px]">
              <div>Created: <b>{result.created}</b></div>
              <div>Updated: <b>{result.updated}</b></div>
              <div>Skipped: <b>{result.skipped}</b></div>
              {result.errors?.length > 0 && (
                <div className="mt-2">
                  <div className="font-semibold">Errors ({result.errors.length}):</div>
                  <ul className="text-[10px] list-disc pl-4">
                    {result.errors.slice(0, 10).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={result ? onDone : onClose}>{result ? "Close" : "Cancel"}</Button>
          {!result && (
            <Button onClick={run} disabled={importing || !file} data-testid="inf-import-run">
              {importing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Import
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Iter 253z-23 · AI discovery + Rate benchmark ─────────────

function AiDiscoveryDialog({ wsId, onClose }) {
  const [mode, setMode] = useState("prompt");   // "prompt" | "lookalike"
  const [prompt, setPrompt] = useState("");
  const [provider, setProvider] = useState("claude");
  const [minF, setMinF] = useState(""); const [maxF, setMaxF] = useState("");
  const [city, setCity] = useState("");
  // Feb 2026 · Lookalike mode inputs
  const [seedQuery, setSeedQuery] = useState("");
  const [seedResults, setSeedResults] = useState([]);
  const [seedPicked, setSeedPicked] = useState(null);    // {id, name, primary_handle, peak_followers}
  const [pctRange, setPctRange] = useState(50);          // ±%
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Debounced seed search when in lookalike mode
  useEffect(() => {
    if (mode !== "lookalike" || !wsId) return;
    if (!seedQuery.trim()) { setSeedResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/workspaces/${wsId}/influencers`, {
          params: { q: seedQuery, limit: 8 },
        });
        setSeedResults(data?.rows || data || []);
      } catch { setSeedResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [seedQuery, wsId, mode]);

  const run = async () => {
    if (mode === "prompt" && !prompt) { toast.error("Describe what you're looking for"); return; }
    if (mode === "lookalike" && !seedPicked) { toast.error("Pick a seed creator to find look-alikes"); return; }
    setBusy(true); setResult(null);
    try {
      const payload = { top_k: 10, provider };
      if (mode === "prompt") {
        payload.prompt = prompt;
        payload.followers_min = Number(minF) || undefined;
        payload.followers_max = Number(maxF) || undefined;
        payload.location_city = city || undefined;
      } else {
        payload.similar_to_influencer_id = seedPicked.id;
        payload.similar_follower_pct = Number(pctRange) || undefined;
      }
      const { data } = await api.post(`/workspaces/${wsId}/influencer-discovery`, payload);
      setResult(data);
    } catch (e) { toast.error(apiErrorMessage(e, "AI find failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="inf-ai-dialog">
        <DialogHeader><DialogTitle>AI creator discovery</DialogTitle></DialogHeader>

        {/* Mode toggle */}
        <div className="inline-flex border border-border" data-testid="inf-ai-mode-toggle">
          {[["prompt", "Prompt-driven"], ["lookalike", "Find look-alikes"]].map(([k, l]) => (
            <button
              key={k}
              type="button"
              onClick={() => { setMode(k); setResult(null); }}
              className={`px-3 py-1 text-[11px] uppercase tracking-widest font-semibold ${mode === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              data-testid={`inf-ai-mode-${k}`}
            >{l}</button>
          ))}
        </div>

        <div className="space-y-3 text-xs">
          {mode === "prompt" ? (
            <>
              <div><Label className="text-[10px] uppercase">Describe your ideal creator</Label>
                <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
                          placeholder="e.g. Lifestyle creators 100k-500k in Mumbai focused on fashion + skincare"
                          className="w-full text-xs px-2 py-1 bg-background border border-border resize-y"
                          data-testid="inf-ai-prompt" />
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div><Label className="text-[10px] uppercase">Followers min</Label><Input type="number" value={minF} onChange={(e) => setMinF(e.target.value)} className="h-9 text-xs" /></div>
                <div><Label className="text-[10px] uppercase">Followers max</Label><Input type="number" value={maxF} onChange={(e) => setMaxF(e.target.value)} className="h-9 text-xs" /></div>
                <div><Label className="text-[10px] uppercase">City</Label><Input value={city} onChange={(e) => setCity(e.target.value)} className="h-9 text-xs" /></div>
                <div>
                  <Label className="text-[10px] uppercase">Model</Label>
                  <Select value={provider} onValueChange={setProvider}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="claude">Claude</SelectItem>
                      <SelectItem value="gpt">GPT 5.2</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <Label className="text-[10px] uppercase">Seed creator (find creators similar to…)</Label>
                {seedPicked ? (
                  <div className="border border-primary bg-primary/5 p-2 flex items-center gap-2">
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{seedPicked.name}</div>
                      <div className="text-[10px] text-muted-foreground">
                        @{seedPicked.primary_handle} · {fmtFollowers(seedPicked.peak_followers)} · {seedPicked.tier || "—"}
                      </div>
                    </div>
                    <button className="text-xs text-primary underline" onClick={() => { setSeedPicked(null); setSeedResults([]); setSeedQuery(""); }} data-testid="inf-ai-seed-clear">
                      Change
                    </button>
                  </div>
                ) : (
                  <>
                    <Input value={seedQuery} onChange={(e) => setSeedQuery(e.target.value)}
                            placeholder="Type name or handle to search…" className="h-9 text-xs"
                            data-testid="inf-ai-seed-search" autoFocus />
                    {seedResults.length > 0 && (
                      <div className="border border-border max-h-52 overflow-y-auto divide-y divide-border mt-1" data-testid="inf-ai-seed-results">
                        {seedResults.map((r) => (
                          <button key={r.id} type="button" onClick={() => setSeedPicked(r)}
                                   className="w-full text-left px-2 py-1.5 hover:bg-muted text-xs"
                                   data-testid={`inf-ai-seed-${r.id}`}>
                            <div className="font-semibold">{r.name}</div>
                            <div className="text-[10px] text-muted-foreground">@{r.primary_handle} · {fmtFollowers(r.peak_followers)} · {(r.niches || []).slice(0, 3).join(", ")}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              {seedPicked && (
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase">± Follower range (%)</Label>
                    <Input type="number" min="10" max="500" value={pctRange} onChange={(e) => setPctRange(e.target.value)} className="h-9 text-xs" data-testid="inf-ai-pct-range" />
                    <div className="text-[9px] text-muted-foreground mt-0.5">± {pctRange}% around {fmtFollowers(seedPicked.peak_followers)}</div>
                  </div>
                  <div className="col-span-2 text-[10px] text-muted-foreground">
                    Look-alikes are ranked using Jaccard similarity on niches, tags,
                    platforms, tier, and country. No AI call needed — deterministic
                    and instant.
                  </div>
                </div>
              )}
            </>
          )}
          {result && (
            <div className="border border-border max-h-[50vh] overflow-y-auto divide-y divide-border" data-testid="inf-ai-result">
              <div className="px-2 py-1 bg-muted/40 text-[10px]">
                {result.mode === "lookalike" ? (
                  <>Look-alikes around <b>@{result.seed?.handle}</b> · {result.candidates} candidates scanned</>
                ) : (
                  <>Ranked from {result.candidates} candidates</>
                )}
              </div>
              {result.shortlist.length === 0 && <div className="p-3 text-muted-foreground">No matches — try widening the filters.</div>}
              {result.shortlist.map((s, i) => (
                <div key={s.id} className="p-3 flex items-start gap-3" data-testid={`inf-ai-pick-${s.id}`}>
                  <div className="w-6 text-right text-[10px] font-mono text-muted-foreground">#{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Link to={`/app/influencers/${s.id}`} className="font-medium hover:text-primary" onClick={onClose}>{s.creator?.name}</Link>
                      <span className="text-[10px] text-muted-foreground">@{s.creator?.primary_handle}</span>
                      <span className="text-[10px] text-emerald-700 font-semibold">score {s.score}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{fmtFollowers(s.creator?.peak_followers)} · {s.creator?.tier} · {(s.creator?.niches || []).join(", ")}</div>
                    <div className="text-[11px] mt-1">{s.rationale}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={run} disabled={busy || (mode === "prompt" ? !prompt : !seedPicked)} data-testid="inf-ai-run">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}Find
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RateBenchmarkDialog({ wsId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/workspaces/${wsId}/influencer-rate-benchmark`).then((r) => setData(r.data)).catch(() => setData({ tiers: [], niches: [] }));
  }, [wsId]);
  const fmt = (v) => v ? `₹${(v / 1000).toFixed(0)}K` : "—";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="inf-bench-dialog">
        <DialogHeader><DialogTitle>Rate card benchmark — medians from your directory</DialogTitle></DialogHeader>
        {!data ? <div className="p-6 text-xs text-muted-foreground">Loading…</div> : (
          <div className="space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">By tier</div>
              <table className="w-full text-xs border border-border">
                <thead className="bg-muted/40">
                  <tr className="text-left text-[10px] uppercase text-muted-foreground">
                    <th className="px-2 py-1">Tier</th><th className="px-2 py-1 text-right">Post</th>
                    <th className="px-2 py-1 text-right">Reel</th><th className="px-2 py-1 text-right">Story</th>
                    <th className="px-2 py-1 text-right">Video</th><th className="px-2 py-1 text-right">n</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.tiers.map((t) => (
                    <tr key={t.tier}>
                      <td className="px-2 py-1 capitalize">{t.tier}</td>
                      <td className="px-2 py-1 text-right font-mono">{fmt(t.medians.per_post_inr)}</td>
                      <td className="px-2 py-1 text-right font-mono">{fmt(t.medians.per_reel_inr)}</td>
                      <td className="px-2 py-1 text-right font-mono">{fmt(t.medians.per_story_inr)}</td>
                      <td className="px-2 py-1 text-right font-mono">{fmt(t.medians.per_video_inr)}</td>
                      <td className="px-2 py-1 text-right text-muted-foreground">{t.sample_size}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.niches?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">By niche (top 20)</div>
                <table className="w-full text-xs border border-border">
                  <thead className="bg-muted/40">
                    <tr className="text-left text-[10px] uppercase text-muted-foreground">
                      <th className="px-2 py-1">Niche</th><th className="px-2 py-1 text-right">Post</th>
                      <th className="px-2 py-1 text-right">Reel</th><th className="px-2 py-1 text-right">Video</th>
                      <th className="px-2 py-1 text-right">n</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.niches.slice(0, 20).map((n) => (
                      <tr key={n.niche}>
                        <td className="px-2 py-1">{n.niche}</td>
                        <td className="px-2 py-1 text-right font-mono">{fmt(n.medians.per_post_inr)}</td>
                        <td className="px-2 py-1 text-right font-mono">{fmt(n.medians.per_reel_inr)}</td>
                        <td className="px-2 py-1 text-right font-mono">{fmt(n.medians.per_video_inr)}</td>
                        <td className="px-2 py-1 text-right text-muted-foreground">{n.sample_size}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
